import type { InvestorProfile } from "../../types/entities";

interface EmptyStateProps {
  profile: InvestorProfile;
  onOpenImport: () => void;
  onAddManually: () => void;
  onLoadDemo: () => void;
}

export default function EmptyState({
  profile,
  onOpenImport,
  onAddManually,
  onLoadDemo,
}: EmptyStateProps) {
  const firstName = profile.preferredName?.trim() || profile.fullLegalName?.split(" ")[0]?.trim() || "";

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#f7f7f4] px-4">
      {/* Wordmark */}
      <div className="mb-10 text-2xl font-bold tracking-tight text-neutral-900">
        AusProp
      </div>

      {/* Heading */}
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-neutral-900">
          {firstName ? `Welcome, ${firstName}.` : "Welcome."}
        </h1>
        <p className="mt-2 text-base text-neutral-500">
          Let's set up your portfolio.
        </p>
      </div>

      {/* Two option cards */}
      <div className="flex w-full max-w-2xl flex-col gap-4 sm:flex-row">
        {/* Card A — Import */}
        <button
          onClick={onOpenImport}
          className="flex flex-1 flex-col items-start gap-4 rounded-2xl border-2 border-neutral-200 bg-white p-6 text-left transition-all hover:border-neutral-900 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-neutral-300"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-neutral-100">
            <UploadIcon />
          </div>
          <div>
            <h2 className="text-base font-semibold text-neutral-900">
              Import my existing portfolio
            </h2>
            <p className="mt-1 text-sm text-neutral-500">
              Upload a spreadsheet, CSV or PDF — we'll extract your properties automatically.
            </p>
          </div>
          <span className="mt-auto text-sm font-medium text-neutral-900">
            Import now →
          </span>
        </button>

        {/* Card B — Manual */}
        <button
          onClick={onAddManually}
          className="flex flex-1 flex-col items-start gap-4 rounded-2xl border-2 border-neutral-200 bg-white p-6 text-left transition-all hover:border-neutral-900 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-neutral-300"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-neutral-100">
            <PlusIcon />
          </div>
          <div>
            <h2 className="text-base font-semibold text-neutral-900">
              Add my first property manually
            </h2>
            <p className="mt-1 text-sm text-neutral-500">
              Enter your property details step by step.
            </p>
          </div>
          <span className="mt-auto text-sm font-medium text-neutral-900">
            Get started →
          </span>
        </button>
      </div>

      {/* Demo link */}
      <button
        onClick={onLoadDemo}
        className="mt-8 text-sm text-neutral-400 underline-offset-2 hover:text-neutral-600 hover:underline transition-colors"
      >
        Explore a sample portfolio instead →
      </button>
    </div>
  );
}

function UploadIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-neutral-600"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-neutral-600"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}
